import React from 'react';
import { Link } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTriangleExclamation } from '@fortawesome/free-solid-svg-icons';

import {
    modeLimitBlockedTitle,
    modeLimitContextHint,
    modeLimitRemediationLine,
    modeLimitUsageLine,
    modeLimitWhyLine,
    resolveSafeModeLimitUpgradeUrl,
    type ModeLimitContext,
    type ModeLimitDetail,
    type ModeLimitSurface,
} from '../utils/modeLimitMessaging';

export type ModeLimitBlockProps = {
    detail: ModeLimitDetail;
    context: ModeLimitContext;
    surface?: ModeLimitSurface;
    onChooseBuiltInMode?: () => void;
    className?: string;
};

const defaultClassName =
    'rounded-lg border border-amber-500/30 bg-amber-500/10 px-3 py-3 text-xs text-theme flex flex-col gap-3';

const ctaClassName =
    'inline-flex items-center justify-center px-3 py-1.5 rounded-lg bg-amber-500 text-black font-semibold text-[11px] hover:bg-amber-400 transition-colors';

const secondaryCtaClassName =
    'inline-flex items-center justify-center px-3 py-1.5 rounded-lg border border-amber-400/40 text-theme font-semibold text-[11px] hover:bg-amber-500/10 transition-colors';

/**
 * Explain-then-fallback for custom-mode limits at point of block (FR35).
 */
const ModeLimitBlock: React.FC<ModeLimitBlockProps> = ({
    detail,
    context,
    surface = 'app',
    onChooseBuiltInMode,
    className = defaultClassName,
}) => {
    const usageLine = modeLimitUsageLine(detail);
    const contextHint = modeLimitContextHint(context);
    const remediationLine = modeLimitRemediationLine(surface);
    const { href: billingHref, useExternal: useExternalBilling } = resolveSafeModeLimitUpgradeUrl(
        detail.upgrade_url,
    );

    return (
        <div
            className={className}
            role="status"
            aria-live="polite"
            data-testid="MODE_LIMIT_BLOCK"
        >
            <div className="flex items-start gap-2">
                <FontAwesomeIcon icon={faTriangleExclamation} className="mt-0.5 shrink-0" aria-hidden />
                <div className="min-w-0 space-y-1">
                    <div className="font-semibold text-theme">{modeLimitBlockedTitle(context)}</div>
                    <p className="text-theme leading-snug">{modeLimitWhyLine(context)}</p>
                    <p className="text-theme leading-snug">{detail.message}</p>
                    {usageLine ? <p className="text-theme-secondary">{usageLine}</p> : null}
                    {contextHint ? <p className="text-theme-secondary">{contextHint}</p> : null}
                    <p className="text-theme-secondary">{remediationLine}</p>
                </div>
            </div>
            {surface === 'onboarding' ? (
                <div className="flex flex-wrap justify-end gap-2">
                    <button
                        type="button"
                        onClick={onChooseBuiltInMode}
                        className={ctaClassName}
                        data-testid="MODE_LIMIT_CHOOSE_BUILTIN"
                    >
                        Choose a built-in mode
                    </button>
                </div>
            ) : (
                <div className="flex flex-wrap justify-end gap-2">
                    <Link to="/app/profile" className={secondaryCtaClassName} data-testid="MODE_LIMIT_MANAGE_MODES">
                        Manage modes
                    </Link>
                    {useExternalBilling ? (
                        <a href={billingHref} className={ctaClassName} data-testid="MODE_LIMIT_OPEN_BILLING">
                            Open Billing
                        </a>
                    ) : (
                        <Link to={billingHref} className={ctaClassName} data-testid="MODE_LIMIT_OPEN_BILLING">
                            Open Billing
                        </Link>
                    )}
                </div>
            )}
        </div>
    );
};

export default ModeLimitBlock;
